import type {
  BaseRecord,
  CreateParams,
  CustomParams,
  DataProvider,
  GetListParams,
  GetOneParams,
  HttpError,
  UpdateParams,
} from '@refinedev/core';

export class AdminApiError extends Error implements HttpError {
  readonly statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.name = 'AdminApiError';
    this.statusCode = statusCode;
  }
}

export type AdminApi = {
  get<T>(path: string): Promise<T>;
  send<T>(method: 'POST' | 'PATCH', path: string, payload: unknown): Promise<T>;
};

export function createAdminApi(
  baseUrl: string,
  getToken: () => Promise<string | null>,
): AdminApi {
  const origin = new URL(baseUrl);
  if (origin.protocol !== 'https:' && origin.hostname !== 'localhost') {
    throw new TypeError(
      'The PipetinAI admin API must use HTTPS outside localhost',
    );
  }

  async function request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    payload?: unknown,
  ): Promise<T> {
    const token = await getToken();
    if (!token) throw new AdminApiError(401, 'Your admin session has expired');
    const response = await fetch(new URL(path, origin), {
      method,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${token}`,
        ...(payload === undefined
          ? {}
          : { 'content-type': 'application/json' }),
      },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => undefined);
      const message =
        errorMessage(body) ?? `PipetinAI returned HTTP ${response.status}`;
      throw new AdminApiError(response.status, message);
    }
    return (await response.json()) as T;
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    send: <T>(method: 'POST' | 'PATCH', path: string, payload: unknown) =>
      request<T>(method, path, payload),
  };
}

export function createDataProvider(
  api: AdminApi,
  baseUrl: string,
): DataProvider {
  const path = (resource: string, id?: string | number) =>
    `/admin/v1/${resource}${id === undefined ? '' : `/${id}`}`;
  return {
    getApiUrl: () => baseUrl,
    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams,
    ) => {
      const value = await api.get<unknown>(path(params.resource));
      const data = records(value) as TData[];
      return { data, total: data.length };
    },
    getOne: async <TData extends BaseRecord = BaseRecord>(
      params: GetOneParams,
    ) => ({
      data: record(
        await api.get<unknown>(path(params.resource, params.id)),
      ) as TData,
    }),
    create: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: CreateParams<TVariables>,
    ) => ({
      data: record(
        await api.send<unknown>(
          'POST',
          path(params.resource),
          params.variables,
        ),
      ) as TData,
    }),
    update: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: UpdateParams<TVariables>,
    ) => ({
      data: record(
        await api.send<unknown>(
          'PATCH',
          path(params.resource, params.id),
          params.variables,
        ),
      ) as TData,
    }),
    deleteOne: () =>
      Promise.reject(
        new AdminApiError(
          405,
          'Delete operations require an explicit platform workflow',
        ),
      ),
    custom: async <
      TData extends BaseRecord = BaseRecord,
      TQuery = unknown,
      TPayload = unknown,
    >(
      params: CustomParams<TQuery, TPayload>,
    ) => {
      const value =
        params.method === 'get'
          ? await api.get<unknown>(params.url)
          : await api.send<unknown>(
              params.method === 'patch' ? 'PATCH' : 'POST',
              params.url,
              params.payload,
            );
      return { data: record(value) as TData };
    },
  };
}

function records(value: unknown): BaseRecord[] {
  if (Array.isArray(value))
    return value.map((entry, index) => record(entry, index));
  return [record(value)];
}

function record(value: unknown, fallback = 0): BaseRecord {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const item = value as Record<string, unknown>;
    return {
      id: typeof item['id'] === 'string' ? item['id'] : fallback,
      ...item,
    };
  }
  return { id: fallback, value };
}

function errorMessage(value: unknown): string | undefined {
  if (value === null || typeof value !== 'object') return undefined;
  const error = (value as Record<string, unknown>)['error'];
  if (error === null || typeof error !== 'object') return undefined;
  const message = (error as Record<string, unknown>)['message'];
  return typeof message === 'string' ? message : undefined;
}
